import classNames from 'classnames'
import ClearButton from 'components/ClearButton'
import IconChevron from 'components/partials/IconChevron'
import useUrlParams from 'hooks/useUrlParams'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

function Dropdown({ id, displayName, filters, disabled, clearAction, replaceFilter = "false" }) {
  const { t } = useTranslation()
  const { urlParams, updateUrlParams, replaceUrlParams } = useUrlParams()
  const [activeIndex, setActiveIndex] = useState(-1)
  const [showState, setShowState] = useState(false)
  const [facetValue, setFacetValue] = useState()
  const [placeholder, setPlaceholder] = useState(
    `${t('Choose')} ${displayName?.toLowerCase()}`
  )
  const domId = id.replace(/\s+/g, '-')?.toLowerCase()
  const dropDown = useRef()

  useEffect(() => {
    const { displayName } = filters.find((f) => f.selected)
    setFacetValue(displayName || '')
  }, [filters])

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      setShowState(!showState && !disabled) // open if not disabled, always possible to close
    }

    if (showState) {
      switch (event.key) {
        case 'Enter':
          const { displayName, query } = filters[activeIndex] || {}
          if (query) updateUrlParams({ [id]: displayName })
          return setFacetValue(displayName || '')
        case 'Tab':
          return setShowState(false)
        case 'ArrowDown':
          event.preventDefault()
          return setActiveIndex(
            activeIndex >= filters.length - 1 ? 0 : activeIndex + 1
          )
        case 'ArrowUp':
          event.preventDefault()
          return setActiveIndex(
            activeIndex === 0 ? filters.length - 1 : activeIndex - 1
          )
        case 'Esc': // IE/Edge specific value
        case 'Escape':
          return setShowState(false)
      }
    }
  }

  const handlePick = ({ displayName, value }) => {
    setFacetValue(displayName)
    setShowState(false)
    if (replaceFilter === "true") {
      replaceUrlParams({
        [id]: value,
        q: urlParams.q,
      })
    } else {
      updateUrlParams({
        [id]: value,
        q: urlParams.q,
      })
    }
  }

  useEffect(() => {
    if (showState) {
      dropDown.current.setAttribute(
        'aria-activedescendant',
        activeIndex > -1 ? `filter--${domId}__${activeIndex}` : ''
      )
    } else {
      dropDown.current.removeAttribute('aria-activedescendant')
    }
  }, [showState, activeIndex])

  return (
    <div className="u-max-width-full u-mt-150 u-mt-200-medium-up">
      {!!displayName && (
        <div className="label u-text-uppercase u-mb-50" id={domId}>
          {displayName}
        </div>
      )}

      <div
        className={classNames(
          'dropdown',
          'dropdown--small',
          { 'is-open': showState },
          { 'is-disabled': disabled }
        )}
        tabIndex={0}
        ref={dropDown}
        role="listbox"
        aria-haspopup="listbox"
        aria-expanded={showState}
        onClick={() => !disabled && !showState && setShowState(true)}
        onKeyDown={(event) => handleKeyDown(event)}
        onBlurCapture={() => setShowState(false)}
        aria-label={`${t('Choose')} ${displayName.toLowerCase()}`}
      >
        <div className="dropdown__inner">
          <div className="dropdown__list dropdown__list--single">
            <div className="dropdown__item">{facetValue || placeholder}</div>
          </div>
        </div>
        {!!facetValue && clearAction ? (
          <ClearButton
            modifierClasses="dropdown__clear"
            label={`${t('Clear selection')}`}
            onClick={(e) => {
              e.stopPropagation()
              setShowState(false)
              setPlaceholder(`${t('Choose')} ${displayName.toLowerCase()}`)
              setFacetValue('')
              clearAction()
            }}
          />
        ) : (
          <IconChevron />
        )}
        <div
          className={classNames('dropdown__list', 'dropdown__list--dropdown', {
            'is-active': showState,
          })}
        >
          {showState && (
            <ul
              className="dropdown__list"
              role="listbox"
              aria-expanded={showState}
            >
              {filters.map(
                ({ displayName, value, selected, disabled }, index) => (
                  <li
                    className={classNames(
                      'dropdown__item dropdown__item--selectable',
                      { 'is-disabled': disabled },
                      { 'is-highlighted': index === activeIndex },
                      { 'u-font-weight-700': selected }
                    )}
                    role="option"
                    aria-selected={selected}
                    aria-label={displayName}
                    id={`filter--${domId}__${index}`}
                    onClick={() => handlePick({ displayName, value })}
                  >
                    {displayName}
                  </li>
                )
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

export default Dropdown
